import { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import type { ThemePreference } from '@neuroedge/shared';
import { Button, NavIcon, type NavIconName } from '../components/ui';
import { NotificationBell } from '../components/notifications/NotificationBell';
import { EmailVerificationBanner } from '../components/auth/EmailVerificationBanner';
import { BillingStatusBanner } from '../components/billing/BillingStatusBanner';
import { CommandPalette } from '../components/command-palette/CommandPalette';
import { QuickActionsMenu } from '../components/quick-actions/QuickActionsMenu';
import { useAuth } from '../auth/AuthContext';
import { useTheme } from '../theme/ThemeContext';
import { useLiveUpdates } from '../realtime/live-updates';
import { useConversations } from '../api/conversations';
import logo from '../theme/NeuroEdgeLogo.png';
import './app-layout.css';

const THEME_OPTIONS: { value: ThemePreference; label: string }[] = [
  { value: 'DARK', label: '🌙 Dark' },
  { value: 'LIGHT', label: '☀️ Light' },
  { value: 'AUTO', label: '🖥️ Auto' },
];

interface NavItem {
  to: string;
  label: string;
  end?: boolean;
  /** Set only for the Inbox link - shows the unread-conversation count. */
  badge?: 'unread-conversations';
  /** Platform-controlled tenant entitlement key - hidden unless the tenant
   * has been granted this menu (see `useAuth().hasMenuAccess`). Items
   * without one (Dashboard, Team, Billing, the whole Admin group) are
   * purely permission-gated, unaffected by this axis. */
  menuKey?: string;
  icon: NavIconName;
}
interface NavGroup {
  label: string;
  items: NavItem[];
}

/** Rendered on its own, above every group - not grouped, always visible. */
const TOP_LEVEL_NAV: NavItem[] = [{ to: '/', label: 'Dashboard', end: true, icon: 'grid' }];

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Sales',
    items: [
      { to: '/contacts', label: 'Contacts', menuKey: 'contacts', icon: 'user' },
      { to: '/leads', label: 'Leads', menuKey: 'leads', icon: 'target' },
      { to: '/pipelines', label: 'Pipelines', menuKey: 'pipelines', icon: 'columns' },
    ],
  },
  {
    label: 'Work',
    items: [
      { to: '/tasks', label: 'Tasks', menuKey: 'tasks', icon: 'checkSquare' },
      { to: '/follow-ups', label: 'Follow-ups', menuKey: 'follow_ups', icon: 'bell' },
      { to: '/appointments', label: 'Appointments', menuKey: 'appointments', icon: 'calendar' },
    ],
  },
  {
    label: 'Messaging',
    items: [
      {
        to: '/conversations',
        label: 'Inbox',
        badge: 'unread-conversations',
        menuKey: 'conversations',
        icon: 'messageCircle',
      },
      { to: '/channels', label: 'Channels', menuKey: 'channels', icon: 'radio' },
      {
        to: '/automation-templates',
        label: 'Templates',
        menuKey: 'automation_templates',
        icon: 'fileText',
      },
      { to: '/automations', label: 'Automations', menuKey: 'automations', icon: 'zap' },
      {
        to: '/automation-flows',
        label: 'Conversation Flows',
        menuKey: 'automation_flows',
        icon: 'gitBranch',
      },
      { to: '/campaigns', label: 'Campaigns', menuKey: 'campaigns', icon: 'megaphone' },
    ],
  },
  {
    label: 'Commerce',
    items: [
      {
        to: '/products-services',
        label: 'Products & Services',
        menuKey: 'products_services',
        icon: 'package',
      },
      { to: '/orders', label: 'Orders', menuKey: 'orders', icon: 'shoppingCart' },
      { to: '/quotations', label: 'Quotations', menuKey: 'quotations', icon: 'clipboard' },
    ],
  },
  {
    label: 'Settings',
    items: [
      { to: '/tags', label: 'Tags', menuKey: 'tags', icon: 'tag' },
      { to: '/custom-fields', label: 'Custom fields', menuKey: 'custom_fields', icon: 'sliders' },
    ],
  },
];

const COLLAPSE_STORAGE_KEY = 'ne.sidebar.collapsedGroups';

function loadCollapsedGroups(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(COLLAPSE_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

function saveCollapsedGroups(value: Record<string, boolean>) {
  try {
    localStorage.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Per-viewer convenience only (remembered open/closed sections) - a
    // private window or blocked storage just means every section starts
    // expanded next time, never a functional problem.
  }
}

function findActiveGroupLabel(pathname: string, groups: NavGroup[]): string | undefined {
  return groups.find((g) =>
    g.items.some((item) => (item.end ? pathname === item.to : pathname.startsWith(item.to))),
  )?.label;
}

/**
 * Application shell: sidebar nav + topbar identity + content. Below 720px the
 * sidebar becomes an off-canvas panel opened by the topbar's hamburger button
 * (see app-layout.css) - closed via its own close button, the backdrop, or by
 * navigating.
 */
export function AppLayout() {
  const { user, tenant, logout, isPlatformAdmin, hasPermission, hasMenuAccess } = useAuth();
  const { mode, setMode } = useTheme();
  // One live-updates stream per signed-in tab (Inbox, bell, dashboard, AI jobs).
  useLiveUpdates(Boolean(user));
  const [menuOpen, setMenuOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const location = useLocation();

  const unreadConversations = useConversations({ unreadOnly: true, pageSize: 1 });
  const unreadCount = unreadConversations.data?.meta.total ?? 0;

  const groups: NavGroup[] = useMemo(() => {
    const settingsExtras: NavItem[] = [
      ...(hasPermission('settings.read')
        ? ([{ to: '/settings/company', label: 'Company profile', icon: 'building' }] as NavItem[])
        : []),
      ...(hasPermission('users.read')
        ? ([{ to: '/team', label: 'Team', icon: 'users' }] as NavItem[])
        : []),
      ...(hasPermission('billing.read')
        ? ([{ to: '/billing', label: 'Billing', icon: 'creditCard' }] as NavItem[])
        : []),
      // What each plan includes - for every member, no permission needed.
      { to: '/plans', label: 'Plans', icon: 'layers' },
      // Gateway to unlock more menus - visible regardless of menu entitlement
      // (only permission-gated), since it's how a tenant gets more of it.
      ...(hasPermission('settings.manage')
        ? ([{ to: '/request-access', label: 'Request Access', icon: 'unlock' }] as NavItem[])
        : []),
    ];
    return [
      ...NAV_GROUPS.map((g) => ({
        ...g,
        items: [
          ...g.items.filter((item) => hasMenuAccess(item.menuKey)),
          ...(g.label === 'Settings' ? settingsExtras : []),
        ],
      })),
      ...(isPlatformAdmin
        ? [
            {
              label: 'Admin',
              items: [
                { to: '/admin/dashboard', label: 'Dashboard', icon: 'barChart' },
                { to: '/admin/tenants', label: 'Tenant Management', icon: 'building' },
                { to: '/admin/access-requests', label: 'Access Requests', icon: 'checkCircle' },
                { to: '/admin/plans', label: 'Plans', icon: 'layers' },
                {
                  to: '/admin/plan-change-requests',
                  label: 'Plan Change Requests',
                  icon: 'refreshCw',
                },
                {
                  to: '/admin/automation-templates',
                  label: 'Automation templates',
                  icon: 'fileText',
                },
                { to: '/admin/automation-flows', label: 'Conversation flows', icon: 'gitBranch' },
                { to: '/admin/error-logs', label: 'Error Logs', icon: 'alertTriangle' },
                {
                  to: '/admin/background-jobs',
                  label: 'Background jobs',
                  icon: 'refreshCw',
                },
                { to: '/admin/platform-admins', label: 'Platform admins', icon: 'users' },
                { to: '/admin/ai-usage', label: 'AI usage', icon: 'barChart' },
                {
                  to: '/admin/platform-messaging',
                  label: 'Platform messaging',
                  icon: 'messageCircle',
                },
              ],
            } satisfies NavGroup,
          ]
        : []),
    ];
  }, [hasPermission, hasMenuAccess, isPlatformAdmin]);

  const [collapsedGroups, setCollapsedGroups] =
    useState<Record<string, boolean>>(loadCollapsedGroups);
  useEffect(() => {
    saveCollapsedGroups(collapsedGroups);
  }, [collapsedGroups]);

  /** Accordion: opening one group collapses every other group. Closing the
   * open group just collapses it - there's no requirement that one stays
   * open. */
  function toggleGroup(label: string) {
    setCollapsedGroups((prev) => {
      const next: Record<string, boolean> = {};
      const willOpen = Boolean(prev[label]);
      for (const g of groups) next[g.label] = g.label === label ? !willOpen : true;
      return next;
    });
  }

  /** A collapsed group containing the current route still shows it - never
   * hide the page you're already on behind a closed section. */
  function isGroupOpen(group: NavGroup): boolean {
    if (!collapsedGroups[group.label]) return true;
    return group.items.some((item) =>
      item.end ? location.pathname === item.to : location.pathname.startsWith(item.to),
    );
  }

  // Navigating to a page inside a group (clicking any nav link) auto-closes
  // every other group, same accordion behaviour as clicking a group header.
  useEffect(() => {
    const activeLabel = findActiveGroupLabel(location.pathname, groups);
    if (!activeLabel) return;
    setCollapsedGroups(() => {
      const next: Record<string, boolean> = {};
      for (const g of groups) next[g.label] = g.label !== activeLabel;
      return next;
    });
  }, [location.pathname, groups]);

  // Global Ctrl+K / Cmd+K command palette - works from anywhere in the app.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(true);
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  // Close on navigation as a *reaction* to the route actually changing,
  // rather than in each NavLink's own onClick. Closing the menu and starting
  // a navigation from the very same click event committed the panel's
  // `right` transition and a large content swap together in one React
  // commit, which left some browsers unable to correctly repaint the panel's
  // position on the *next* open (closing via the header's close button,
  // which never touches navigation, always worked correctly).
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Lock background scroll while the off-canvas menu is open. Besides being
  // standard mobile-menu UX (no scroll-through behind the panel), this also
  // works around a real mobile-browser quirk where a `position: fixed`
  // element can render incorrectly on a page that is scrolled/scrollable -
  // Dashboard (short, no scroll) opened the menu fine; taller pages didn't.
  // Removing scroll as a variable while the panel is shown avoids that.
  useEffect(() => {
    if (!menuOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [menuOpen]);

  return (
    <div className="app-shell">
      {menuOpen ? (
        <div
          className="app-shell__backdrop"
          onClick={() => setMenuOpen(false)}
          aria-hidden="true"
        />
      ) : null}
      <aside
        className={menuOpen ? 'app-shell__sidebar app-shell__sidebar--open' : 'app-shell__sidebar'}
      >
        <div className="app-shell__sidebar-head">
          <div className="app-shell__brand">
            <img src={logo} alt="" className="app-shell__brand-logo" />
            NeuroEdge Connect
          </div>
          <button
            type="button"
            className="app-shell__panel-close"
            aria-label="Close menu"
            onClick={() => setMenuOpen(false)}
          >
            ✕
          </button>
        </div>
        <nav className="app-shell__nav">
          {TOP_LEVEL_NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                isActive ? 'app-shell__link app-shell__link--active' : 'app-shell__link'
              }
            >
              <span className="app-shell__link-main">
                <NavIcon name={item.icon} className="app-shell__link-icon" />
                {item.label}
              </span>
            </NavLink>
          ))}
          {groups.map((group) => {
            const open = isGroupOpen(group);
            return (
              <div
                key={group.label}
                className={
                  open
                    ? 'app-shell__nav-group'
                    : 'app-shell__nav-group app-shell__nav-group--collapsed'
                }
              >
                <button
                  type="button"
                  className="app-shell__nav-group-header"
                  aria-expanded={open}
                  onClick={() => toggleGroup(group.label)}
                >
                  {group.label}
                  <span className="app-shell__nav-group-chevron" aria-hidden="true">
                    ▾
                  </span>
                </button>
                {open ? (
                  <div className="app-shell__nav-group-items">
                    {group.items.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.end}
                        className={({ isActive }) =>
                          isActive ? 'app-shell__link app-shell__link--active' : 'app-shell__link'
                        }
                      >
                        <span className="app-shell__link-main">
                          <NavIcon name={item.icon} className="app-shell__link-icon" />
                          {item.label}
                        </span>
                        {item.badge === 'unread-conversations' && unreadCount > 0 ? (
                          <span className="app-shell__nav-badge">{unreadCount}</span>
                        ) : null}
                      </NavLink>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </nav>
      </aside>
      <div className="app-shell__main">
        <header className="app-shell__topbar">
          <button
            type="button"
            className="app-shell__menu-toggle"
            aria-label="Open menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(true)}
          >
            ☰
          </button>
          <span className="app-shell__tenant">{tenant?.name}</span>
          <span className="app-shell__spacer" />
          <QuickActionsMenu />
          <button
            type="button"
            className="app-shell__search-trigger"
            onClick={() => setPaletteOpen(true)}
          >
            <span aria-hidden="true">🔍</span> Search…
            <kbd className="app-shell__search-kbd">Ctrl K</kbd>
          </button>
          <select
            className="app-shell__theme-select"
            aria-label="Theme"
            value={mode}
            onChange={(e) => setMode(e.target.value as ThemePreference)}
          >
            {THEME_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <Link to="/profile" className="app-shell__user">
            {user?.displayName}
          </Link>
          <NotificationBell />
          <Button variant="secondary" onClick={() => void logout()}>
            Sign out
          </Button>
        </header>
        <EmailVerificationBanner />
        <BillingStatusBanner />
        <main className="app-shell__content">
          <Outlet />
        </main>
      </div>
      {paletteOpen ? <CommandPalette onClose={() => setPaletteOpen(false)} /> : null}
    </div>
  );
}
